import { createI18n } from 'vue-i18n'
import type { MessageResolver } from 'vue-i18n'

type LocaleMessages = Record<string, string>

// Auto-load every locale file in ./locales, keyed by its code (filename without
// extension), so adding a new <code>.json is all that's needed to support a new
// language. Uses webpack's require.context (the Vite `import.meta.glob`
// equivalent) since this app is built with vue-cli/webpack.
const localeContext = require.context('../locales', false, /\.json$/)
const messages: Record<string, LocaleMessages> = {}
localeContext.keys().forEach((path: string) => {
  const code = path.replace(/^\.\//, '').replace(/\.json$/, '')
  messages[code] = localeContext(path) as LocaleMessages
})

const SUPPORTED_LOCALES = Object.keys(messages)

// Presentation/UI language codes that map to an existing locale file but use a
// different code in the presenter app.
const LOCALE_ALIASES: Record<string, string> = {
  br: 'pt', // Brazilian Portuguese
  si: 'sl', // Slovenian (alternate code used by the presenter app)
}

// Locale keys are the English source strings (matching the presenter app's flat
// language files) and many contain '.', so look keys up literally instead of
// treating '.' as a nested-path separator (vue-i18n's default behaviour).
const messageResolver: MessageResolver = (obj, key) => {
  if (obj == null || typeof obj !== 'object') return null
  return (obj as Record<string, ReturnType<MessageResolver>>)[key] ?? null
}

const i18n = createI18n({
  legacy: false,
  globalInjection: true, // expose $t in Options-API templates
  locale: 'en',
  fallbackLocale: 'en',
  messages,
  messageResolver,
})

// Switch the UI language for the moderation page. Falls back to English when no
// translation file exists for the given locale.
export const setI18nLocale = (lang?: string | null): void => {
  if (!lang) return
  const resolved = LOCALE_ALIASES[lang] ?? lang
  if (SUPPORTED_LOCALES.includes(resolved)) {
    i18n.global.locale.value = resolved
  }
}

// When embedded in the presenter's Backstage Q&A tab the presenter passes its
// UI language via the `lang` query param (`?embed=true&lang=xx`). Apply it
// immediately on load. The presentation-language fallback (set once the
// presentation has been fetched) covers the standalone, non-embedded case.
setI18nLocale(new URLSearchParams(window.location.search).get('lang'))

export default i18n
